// Recommends a size from height + build (weight relative to height), not height alone.
// Step 1: base size from height. Step 2: adjust upward by weight bracket — a person
// heavy for their height can jump more than one size (e.g. short + heavy -> XL directly).
function getRecommendedSize(heightCm, weightKg) {
  // Under 170 cm: base S, but weight can push it up to M, L, or even XL.
  if (heightCm < 170) {
    if (weightKg >= 85) return 'XL';
    if (weightKg >= 75) return 'L';
    if (weightKg >= 65) return 'M';
    return 'S';
  }

  // 170-175 cm: base M, heavier build pushes to L or XL.
  if (heightCm >= 170 && heightCm <= 175) {
    if (weightKg >= 82) return 'XL';
    if (weightKg >= 72) return 'L';
    return 'M';
  }

  // 176-181 cm: base L, heavier build pushes straight to XL.
  if (heightCm >= 176 && heightCm <= 181) {
    if (weightKg >= 81) return 'XL';
    return 'L';
  }

  // 182 cm and up: XL is both the base and the ceiling — never go past XL.
  return 'XL';
}

var RULER_UNIT_WIDTH = 10; // px per unit of value

class RulerSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.min = parseInt(this.dataset.min, 10);
    this.max = parseInt(this.dataset.max, 10);
    this.majorStep = parseInt(this.dataset.majorStep, 10) || 10;
    this.value = parseInt(this.dataset.value, 10);
    this.unit = this.dataset.unit || '';
    this.label = this.dataset.label || '';

    this.innerHTML =
      '<div class="ruler-slider__header">' +
      '<span class="ruler-slider__label">' + this.label + '</span>' +
      '<span class="ruler-slider__reading"><span data-reading>' + this.value + '</span> ' + this.unit + '</span>' +
      '</div>' +
      '<div class="ruler-slider__pointer" aria-hidden="true">&#8203;</div>' +
      '<div class="ruler-slider__viewport" data-viewport>' +
      '<div class="ruler-slider__track" data-track></div>' +
      '</div>';

    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.reading = this.querySelector('[data-reading]');

    this.buildTicks();

    var self = this;
    requestAnimationFrame(function () {
      self.setPadding();
      self.scrollToValue(self.value, false);
    });

    this.viewport.addEventListener('scroll', function () {
      self.onScroll();
    });

    window.addEventListener('resize', function () {
      self.setPadding();
      self.scrollToValue(self.value, false);
    });

    this.viewport.style.touchAction = 'none';
    this.isDragging = false;
    this.viewport.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.viewport.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.viewport.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.viewport.addEventListener('pointercancel', this.onPointerUp.bind(this));
  }

  onPointerDown(event) {
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.viewport.scrollLeft;
    this.viewport.setPointerCapture(event.pointerId);
    this.viewport.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();
    var delta = event.clientX - this.dragStartX;
    this.viewport.scrollLeft = this.dragStartScrollLeft - delta;
  }

  onPointerUp(event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.viewport.classList.remove('is-dragging');
    try {
      this.viewport.releasePointerCapture(event.pointerId);
    } catch (e) {
      /* no-op */
    }
    this.scrollToValue(this.value, true);
  }

  buildTicks() {
    var frag = document.createDocumentFragment();
    var width = (this.max - this.min) * RULER_UNIT_WIDTH;
    this.track.style.width = width + 'px';

    for (var i = this.min; i <= this.max; i++) {
      var isMajor = i % this.majorStep === 0;
      var isMid = !isMajor && i % (this.majorStep / 2) === 0;
      var tick = document.createElement('div');
      tick.className =
        'ruler-slider__tick' + (isMajor ? ' ruler-slider__tick--major' : isMid ? ' ruler-slider__tick--mid' : '');
      tick.style.left = (i - this.min) * RULER_UNIT_WIDTH + 'px';
      tick.textContent = '​';
      frag.appendChild(tick);

      if (isMajor) {
        var labelEl = document.createElement('span');
        labelEl.className = 'ruler-slider__tick-label';
        labelEl.style.left = (i - this.min) * RULER_UNIT_WIDTH + 'px';
        labelEl.textContent = i;
        frag.appendChild(labelEl);
      }
    }
    this.track.appendChild(frag);
  }

  setPadding() {
    var half = this.viewport.clientWidth / 2;
    this.viewport.style.paddingLeft = half + 'px';
    this.viewport.style.paddingRight = half + 'px';
  }

  onScroll() {
    var self = this;
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(function () {
      var raw = self.viewport.scrollLeft / RULER_UNIT_WIDTH + self.min;
      var clamped = Math.min(self.max, Math.max(self.min, Math.round(raw)));
      if (clamped !== self.value) {
        self.value = clamped;
        self.reading.textContent = clamped;
        self.dispatchEvent(new CustomEvent('ruler-change', { detail: { value: clamped }, bubbles: true }));
      }
    });
  }

  scrollToValue(v, smooth) {
    var left = (v - this.min) * RULER_UNIT_WIDTH;
    this.viewport.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
  }
}
customElements.define('ruler-slider', RulerSlider);

class SizeChecker extends HTMLElement {
  constructor() {
    super();
    this.heightSlider = this.querySelector('[data-height-input]');
    this.weightSlider = this.querySelector('[data-weight-input]');
    this.resultSize = this.querySelector('[data-result-size]');
    this.selectButton = this.querySelector('[data-select-size]');

    this.addEventListener('ruler-change', this.update.bind(this));
    this.selectButton.addEventListener('click', this.selectSize.bind(this));
  }

  update() {
    var height = this.heightSlider.value;
    var weight = this.weightSlider.value;
    this.resultSize.textContent = getRecommendedSize(height, weight);
  }

  selectSize() {
    var size = this.resultSize.textContent.trim();
    var input = document.querySelector('variant-selects input[type="radio"][value="' + size + '"]');
    if (input && !input.disabled) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var modal = this.closest('modal-dialog');
    if (modal) modal.hide();
  }
}

customElements.define('size-checker', SizeChecker);
